// Single source of truth: the same raw svg is hosted for Auth0 (/assets/elephant-logomark.svg),
// used as an <img> elsewhere (?url), and rendered here as a component (@svgr/webpack). Its per-path
// `col*`/`small` classNames are what the pulse animation below targets, so svgo must stay off.
import ElephantLogomarkSvg from "$assets/elephant-logomark.svg";
import { makeStyles } from "@material-ui/core";
import React from "react";

/**
 * This component must remain devoid of any dependencies of our contexts, as it is used in locations
 * outside of many of the core contexts.
 */
export const ElephantLogomark: FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className, style = { maxWidth: 220, maxHeight: 220 } }) => (
  <ElephantLogomarkSvg className={className} style={style} />
);

const useStyles = makeStyles({
  "@keyframes animLg": {
    "0%, 50%": { transform: "scale(1)" },
    "25%": { transform: "scale(0.6)" },
  },
  "@keyframes animSm": {
    "0%, 50%": { transform: "scale(1)" },
    "25%": { transform: "scale(1.1)" },
  },
  logo: {
    "--increment": "0.25s",
    "--duration": "2.75s",
    "& path": {
      animation: "animSm var(--duration) ease-in-out infinite",
      "transform-box": "fill-box",
      "transform-origin": "50% 50%",
    },
    "& path.small": {
      "animation-name": "$animSm",
    },
    "& path:not(.small)": {
      "animation-name": "$animLg",
    },
    "& path.col1": {
      "animation-delay": "animation-delay: 0s",
    },
    "& path.col2": {
      "animation-delay": "calc((var(--increment) * 1))",
    },
    "& path.col3": {
      "animation-delay": "calc((var(--increment) * 2))",
    },
    "& path.col4": {
      "animation-delay": "calc((var(--increment) * 3))",
    },
    "& path.col5": {
      "animation-delay": "calc((var(--increment) * 4))",
    },
    "& path.col6": {
      "animation-delay": "calc((var(--increment) * 5))",
    },
    "& path.col7": {
      "animation-delay": "calc((var(--increment) * 6))",
    },
    "& path.col8": {
      "animation-delay": "calc((var(--increment) * 7))",
    },
    "& path.col9": {
      "animation-delay": "calc((var(--increment) * 8))",
    },
    "& path.col10": {
      "animation-delay": "calc((var(--increment) * 9))",
    },
  },
});

// TODO: Would be nice to push the pulse effect into the SVG, so that it can run outside of MUI
// without needing to manually update style sheets ourselves to apply the keyframes. Sadly you
// can't use keyframes in inline styles.
export const PulsingElephantLogomark: FC<
  React.ComponentProps<typeof ElephantLogomark>
> = (restProps) => {
  const styles = useStyles();
  return <ElephantLogomark className={styles.logo} {...restProps} />;
};
